/**
 * PRISM's side of the PRISM Graphics bridge (protocol version 1): the vendored `@prism-graphics/bridge/host`
 * (vendor/prism-graphics-bridge; 1.0.1 since PRISM 1.0.0-rc.14), plus the little PRISM adds to it. The package checks
 * the source window, the exact editor origin, the protocol version and every payload, never posts to '*', and keeps
 * tokens out of URLs and logs.
 */
import { BridgeError, type HostInit, type Token } from '@prism-graphics/bridge/host';

export { BridgeError, createHost } from '@prism-graphics/bridge/host';
export type { BridgeLogger, Host, HostInit, Token } from '@prism-graphics/bridge/host';

export type EditorFormat = NonNullable<HostInit['defaults']>;

/** host:init as PRISM sends it: the package's HostInit plus the vendor flag `v_duplicate` (Duplicate), which the
 * bridge passes through as an extra key. */
export type PrismHostInit = HostInit & { v_duplicate?: boolean };

/**
 * How long Close waits for the editor's answer before PRISM asks the person itself. Always passed: the bridge's own
 * default since 1.0.1 is DEFAULT_CLOSE_TIMEOUT_MS (300 s), far too long for a Close button.
 */
export const CLOSE_TIMEOUT_MS = 15_000;

const codeOf = (err: unknown): string | null => (err instanceof BridgeError ? err.code : null);

/**
 * What PRISM does when requestClose rejects (bridge 1.0.1): the editor did not answer in time (TIMEOUT), or reloaded
 * before answering (NOT_READY, sent by a new editor:ready) — PRISM then asks the person whether to close it anyway,
 * so the editor never stays stuck open. Anything else (an editor:error, already shown by the error handler) leaves
 * the editor open. Returns the question to ask, or null.
 */
export function closeQuestion(err: unknown): string | null {
  const code = codeOf(err);
  if (code === 'TIMEOUT') return 'PRISM Graphics did not answer. Close it anyway? Unsaved changes are lost.';
  if (code === 'NOT_READY') return 'PRISM Graphics restarted before it answered. Close it anyway? Unsaved changes may be lost.';
  return null;
}

/**
 * What PRISM says when requestSave rejects: nothing when the person cancelled the save or chose not to resolve a
 * version conflict in the editor; for NOT_READY (the editor reloaded before it answered, bridge 1.0.1) and TIMEOUT a
 * sentence saying the save may not have happened; otherwise the error's own message.
 */
export function saveProblem(err: unknown, message: (err: unknown) => string): string | null {
  const code = codeOf(err);
  if (code === 'SAVE_CANCELLED' || code === 'VERSION_CONFLICT_UNRESOLVED') return null;
  if (code === 'NOT_READY') return 'PRISM Graphics restarted before the save finished. Check the graphic, then save again.';
  if (code === 'TIMEOUT') return 'PRISM Graphics did not answer the save. Check the graphic, then save again.';
  return message(err);
}

/** What the editor is opened for, as host:init needs it. */
export interface EditorOpen {
  mode: 'new' | 'edit';
  assetId?: string;
  duplicate?: boolean;
  folderId?: string;
  defaults: EditorFormat;
}

/** The host:init PRISM sends: PRISM is dark only, and the locale is English. */
export function editorInit(open: EditorOpen, user: { id: string; displayName: string }, token: Token): PrismHostInit {
  return {
    token,
    user: { id: user.id, name: user.displayName, locale: 'en' },
    theme: 'dark',
    mode: open.mode,
    ...(open.assetId ? { assetId: open.assetId } : {}),
    ...(open.folderId ? { folderId: open.folderId } : {}),
    ...(open.duplicate ? { v_duplicate: true } : {}),
    defaults: open.defaults,
  };
}
